import React, { useMemo, useState, useEffect, useRef } from 'react';

import { DashboardOrdersTable } from '@/components/DashboardOrdersTable';
import { OrderFilesModal } from '@/components/OrderFilesModal';
import type { Order, OrderStatus } from '@shared/types';
import { supabase } from '@/lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

const PAGE_SIZE = 10;

// Temporarily hide the print-status workflow UI (status filter, To Print/Printed
// stat cards, status column + Mark-printed actions). Flip to `true` to restore.
const SHOW_PRINT_STATUS = false;

// Print-status filter options shown as a segmented control. "To Print" groups
// the unprinted states (pending + printing) — the orders an owner still owes.
type PrintFilter = 'all' | 'unprinted' | 'printed';
const PRINT_FILTERS: { value: PrintFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'unprinted', label: 'To Print' },
  { value: 'printed', label: 'Printed' },
];

interface DateBounds {
  gte?: string;
  lt?: string;
}

const matchesPrintFilter = (order: Order, filter: PrintFilter): boolean => {
  if (filter === 'all') return true;
  if (filter === 'unprinted') return order.print_status === 'pending' || order.print_status === 'printing';
  return order.print_status === 'printed';
};

const getFilterBounds = (filter: string): DateBounds => {
  const now = new Date();
  
  if (filter === 'today') {
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    return {
      gte: startOfToday.toISOString(),
      lt: endOfToday.toISOString(),
    };
  }
  
  if (filter === 'yesterday') {
    const startOfYesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    const endOfYesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return {
      gte: startOfYesterday.toISOString(),
      lt: endOfYesterday.toISOString(),
    };
  }
  
  if (filter === 'week') {
    const dayOfWeek = now.getDay(); // 0 is Sunday
    const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek);
    const endOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() + (7 - dayOfWeek));
    return {
      gte: startOfWeek.toISOString(),
      lt: endOfWeek.toISOString(),
    };
  }
  
  return {};
};

export const DashboardPage: React.FC = () => {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [selectedFilesOrder, setSelectedFilesOrder] = useState<Order | null>(null);
  const [isFilesOpen, setIsFilesOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  // Status breakdown for the summary cards — respects date + search, ignores the
  // active print-status filter so the owner always sees the full split.
  const [stats, setStats] = useState({ toPrint: 0, printed: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [dateFilter, setDateFilter] = useState('today');
  const [printFilter, setPrintFilter] = useState<PrintFilter>('all');
  // Debounced copy of the search box so we hit the DB only after typing settles.
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [markedOrders, setMarkedOrders] = useState<{ id: string; bookmarkedAt: number }[]>(() => {
    try {
      const stored = localStorage.getItem('marked_order_ids_v2');
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return [];
      
      // Filter out bookmarks older than 3 days (3 * 24 * 60 * 60 * 1000 ms)
      const threeDaysInMs = 3 * 24 * 60 * 60 * 1000;
      const now = Date.now();
      return parsed.filter((item: any) => item && item.id && (now - (item.bookmarkedAt || 0) <= threeDaysInMs));
    } catch {
      return [];
    }
  });
  
  const [showBookmarksOnly, setShowBookmarksOnly] = useState(false);

  useEffect(() => {
    localStorage.setItem('marked_order_ids_v2', JSON.stringify(markedOrders));
  }, [markedOrders]);

  const markedOrderIds = useMemo(() => {
    return markedOrders.map((item) => item.id);
  }, [markedOrders]);

  const processedOrders = useMemo(() => {
    return orders.map((order) => ({
      ...order,
      is_marked: markedOrderIds.includes(order.id)
    }));
  }, [orders, markedOrderIds]);

  // The realtime subscription is set up once on mount, so it can't read the
  // latest filter state directly. Mirror what the subscription needs into a ref.
  const viewRef = useRef({ printFilter, currentPage });
  viewRef.current = { printFilter, currentPage };

  // Debounce the search box → only refetch 350ms after the user stops typing.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchValue.trim());
      setCurrentPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [searchValue]);

  // Date, phone search and print status are filtered server-side (so they work
  // across every page). Bookmarks stay client-side over the current page.
  const filteredOrders = useMemo(() => {
    if (!showBookmarksOnly) return processedOrders;
    return processedOrders.filter((order) => markedOrderIds.includes(order.id));
  }, [processedOrders, showBookmarksOnly, markedOrderIds]);

  useEffect(() => {
    // ONLY setup realtime subscription ONCE on mount
    const channel = supabase
      .channel('schema-db-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: admin?.shop_id ? `shop_id=eq.${admin.shop_id}` : undefined,
        },
        (payload) => {
          console.log('Realtime payload:', payload);
          const { printFilter: activeFilter, currentPage: activePage } = viewRef.current;

          if (payload.eventType === 'INSERT') {
            const newOrder = payload.new as Order;
            // Only surface a brand-new order if it belongs in the current view
            // and we're on the first page (where the newest orders live).
            if (activePage !== 0 || !matchesPrintFilter(newOrder, activeFilter)) return;
            setOrders((prev) =>
              prev.some((o) => o.id === newOrder.id) ? prev : [newOrder, ...prev].slice(0, PAGE_SIZE)
            );
            setTotalOrders((prev) => prev + 1);
          } else if (payload.eventType === 'UPDATE') {
            const updatedOrder = payload.new as Order;
            const matches = matchesPrintFilter(updatedOrder, activeFilter);
            setOrders((prev) => {
              const exists = prev.some((order) => order.id === updatedOrder.id);
              if (exists) {
                // If the update pushed it out of the current view (e.g. marked
                // printed while viewing "To Print"), drop it from the list.
                if (!matches) return prev.filter((order) => order.id !== updatedOrder.id);
                // Replace in place, then re-sort by updated_at so an order that
                // just received a new file bubbles back to the top.
                const next = prev.map((order) =>
                  order.id === updatedOrder.id ? updatedOrder : order
                );
                return next.sort(
                  (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
                );
              }
              // Order wasn't on screen (e.g. a file was appended to an older
              // open order). Surface it at the top so received files never go
              // missing — but only if it matches the view and we're on page 1.
              if (matches && activePage === 0) {
                return [updatedOrder, ...prev].slice(0, PAGE_SIZE);
              }
              return prev;
            });
          } else if (payload.eventType === 'DELETE') {
            setOrders((prev) => prev.filter((order) => order.id !== payload.old.id));
            setTotalOrders((prev) => prev - 1);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []); // Empty dependency - only run once on mount

  useEffect(() => {
    // Refetch data when currentPage, refresh, or date filter changes
    const fetchOrders = async () => {
      setIsLoading(true);
      try {
        const offset = currentPage * PAGE_SIZE;
        const bounds = getFilterBounds(dateFilter);
        
        let countQuery = supabase
          .from('dashboard_orders_view')
          .select('id', { count: 'exact', head: true })
          .neq('payment_status', 'archived');
        
        let dataQuery = supabase
          .from('dashboard_orders_view')
          .select('id, shop_id, customer_phone, print_status, created_at, updated_at, file_count, total_pages, pages_calculating')
          .neq('payment_status', 'archived')
          .order('updated_at', { ascending: false })
          .range(offset, offset + PAGE_SIZE - 1);

        if (admin?.shop_id) {
          countQuery = countQuery.eq('shop_id', admin.shop_id);
          dataQuery = dataQuery.eq('shop_id', admin.shop_id);
        }

        // Filter by last activity (updated_at), not created_at. Because new files
        // can be appended to an older still-unprinted order, updated_at is the
        // timestamp that reflects "this order got activity today".
        if (bounds.gte) {
          countQuery = countQuery.gte('updated_at', bounds.gte);
          dataQuery = dataQuery.gte('updated_at', bounds.gte);
        }
        if (bounds.lt) {
          countQuery = countQuery.lt('updated_at', bounds.lt);
          dataQuery = dataQuery.lt('updated_at', bounds.lt);
        }

        // Print-status filter. "To Print" = anything not yet printed.
        if (printFilter === 'unprinted') {
          countQuery = countQuery.in('print_status', ['pending', 'printing']);
          dataQuery = dataQuery.in('print_status', ['pending', 'printing']);
        } else if (printFilter === 'printed') {
          countQuery = countQuery.eq('print_status', 'printed');
          dataQuery = dataQuery.eq('print_status', 'printed');
        }

        // Phone-number search across all orders (not just the current page).
        if (debouncedSearch) {
          const pattern = `%${debouncedSearch}%`;
          countQuery = countQuery.ilike('customer_phone', pattern);
          dataQuery = dataQuery.ilike('customer_phone', pattern);
        }

        // Summary counts (date + search aware, but NOT limited by the active
        // print-status filter) so the cards always show the full split.
        const withBaseFilters = () => {
          let q = supabase
            .from('dashboard_orders_view')
            .select('id', { count: 'exact', head: true })
            .neq('payment_status', 'archived');
          if (admin?.shop_id) q = q.eq('shop_id', admin.shop_id);
          if (bounds.gte) q = q.gte('updated_at', bounds.gte);
          if (bounds.lt) q = q.lt('updated_at', bounds.lt);
          if (debouncedSearch) q = q.ilike('customer_phone', `%${debouncedSearch}%`);
          return q;
        };

        // Get count + summary counts + paginated data together.
        const [{ count }, toPrintRes, printedRes] = await Promise.all([
          countQuery,
          withBaseFilters().in('print_status', ['pending', 'printing']),
          withBaseFilters().eq('print_status', 'printed'),
        ]);
        setStats({ toPrint: toPrintRes.count ?? 0, printed: printedRes.count ?? 0 });

        // Get paginated data
        const { data, error } = await dataQuery;
          
        if (error) {
          console.error('Error fetching orders:', error);
        } else if (data) {
          setOrders(data as unknown as Order[]);
          if (count !== null) setTotalOrders(count);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchOrders();
  }, [currentPage, refreshTrigger, dateFilter, printFilter, debouncedSearch]); // Refetch on page/filter changes



  const openFilesViewer = (order: Order) => {
    setSelectedFilesOrder(order);
    setIsFilesOpen(true);
  };

  const refreshOrders = async () => {
    setCurrentPage(0);
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleMarkOrder = (orderId: string) => {
    setMarkedOrders((prev) => {
      const exists = prev.some((item) => item.id === orderId);
      if (exists) {
        return prev.filter((item) => item.id !== orderId);
      } else {
        return [...prev, { id: orderId, bookmarkedAt: Date.now() }];
      }
    });
  };

  const handleMarkPrinted = async (order: Order) => {
    // Optimistically update the row, then persist.
    setOrders((prev) =>
      prev.map((o) => (o.id === order.id ? { ...o, print_status: 'printed' } : o))
    );

    const { error } = await supabase
      .from('orders')
      .update({ print_status: 'printed', updated_at: new Date().toISOString() })
      .eq('id', order.id);

    if (error) {
      console.error('Failed to mark order as printed:', error);
      // Revert on failure
      setOrders((prev) =>
        prev.map((o) => (o.id === order.id ? { ...o, print_status: order.print_status ?? 'pending' } : o))
      );
    }
  };


  return (
    <>
      <div className="min-h-screen px-4 py-6 lg:px-8">
        <div className="mx-auto max-w-7xl space-y-4">
          {/* Controls Bar */}
          <div className="flex flex-row items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-semibold text-slate-950 mr-auto">Orders</h1>
            
            {refreshOrders && (
              <button
                onClick={refreshOrders}
                disabled={isLoading}
                className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-colors disabled:opacity-50"
                aria-label="Refresh orders"
                title="Refresh"
              >
                <svg 
                  className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="1.5"
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" 
                  />
                </svg>
              </button>
            )}

            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
              <input
                id="search"
                type="search"
                name="search"
                placeholder="Search by Phone Number"
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                className="w-32 hover:w-60 focus:w-60 h-9 pl-9 pr-4 text-sm bg-white border border-slate-200 rounded-lg text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 ease-in-out"
              />
            </div>

            <button
              disabled
              title="Launching soon"
              className="group relative flex items-center justify-center gap-2 h-9 px-4 text-sm bg-slate-50 border border-slate-200 border-dashed rounded-lg text-slate-400 font-medium cursor-not-allowed transition-all"
            >
              <svg className="w-4 h-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span className="opacity-80">Clear Space</span>
            </button>

            <div className="relative inline-flex items-center">
              <span className="absolute left-3 pointer-events-none text-slate-500">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </span>
              <select
                id="dateFilter"
                value={dateFilter}
                onChange={(event) => {
                  setDateFilter(event.target.value);
                  setCurrentPage(0);
                }}
                className="appearance-none w-32 h-9 pl-9 pr-8 text-sm bg-white border border-slate-200 rounded-lg text-slate-700 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer"
                title="Date Filter"
              >
                <option value="today">Today's</option>
                <option value="yesterday">Yesterday's</option>
                <option value="week">This Week</option>
                <option value="all">All Time</option>
              </select>
              <span className="absolute right-3 pointer-events-none text-slate-400">
                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </div>

            {/* Print status segmented control — the owner's "what's left to print" view */}
            {SHOW_PRINT_STATUS && (
              <div className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white p-0.5">
                {PRINT_FILTERS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      setPrintFilter(opt.value);
                      setCurrentPage(0);
                    }}
                    className={`h-8 rounded-md px-3 text-sm font-medium transition-colors ${
                      printFilter === opt.value
                        ? 'bg-slate-900 text-white'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    title={`Show ${opt.label} orders`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            <button
              onClick={() => setShowBookmarksOnly((prev) => !prev)}
              className={`inline-flex items-center gap-2 h-9 px-3 rounded-lg border transition-colors text-sm font-medium ${
                showBookmarksOnly
                  ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
                  : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
              }`}
              title="Saved"
            >
              <svg
                className={`h-4 w-4 ${showBookmarksOnly ? 'text-amber-600' : 'text-slate-500'}`}
                fill={showBookmarksOnly ? 'currentColor' : 'none'}
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              <span>Saved</span>
            </button>
          </div>




          {/* Table */}
          <DashboardOrdersTable
            orders={filteredOrders}
            startIndex={currentPage * PAGE_SIZE}
            totalCount={totalOrders}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            onViewFiles={openFilesViewer}
            onRefresh={refreshOrders}
            onMarkOrder={handleMarkOrder}
            onMarkPrinted={handleMarkPrinted}
            isLoading={isLoading}
          />

          {/* Pagination */}
          <div className="flex items-center justify-between py-4 border-t">
            <div className="text-sm text-slate-600">
              {totalOrders === 0
                ? 'No orders'
                : `Showing ${currentPage * PAGE_SIZE + 1}–${Math.min(
                    (currentPage + 1) * PAGE_SIZE,
                    totalOrders
                  )} of ${totalOrders} orders`}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0 || isLoading}
                className="px-3 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                ← Previous
              </button>
              <span className="px-3 py-1 text-sm text-slate-600">
                Page {currentPage + 1} of {Math.max(1, Math.ceil(totalOrders / PAGE_SIZE))}
              </span>
              <button
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={(currentPage + 1) * PAGE_SIZE >= totalOrders || isLoading}
                className="px-3 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      </div>

      <OrderFilesModal
        isOpen={isFilesOpen}
        order={selectedFilesOrder}
        onClose={() => {
          setIsFilesOpen(false);
          setSelectedFilesOrder(null);
        }}
      />
    </>
  );
};

export default DashboardPage;

